import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Phone, MessageCircle, Navigation, Wrench } from 'lucide-react';
import { trackCallClick, trackMessengerClick, trackDirectionsClick, trackCtaClick } from '../utils/analytics';
import { PRIMARY_PHONE, MESSENGER_URL, isAnyStoreOpenNow, getStoreContact, STORES } from '../constants/stores';
import { useActiveStoreKey } from '../context/ActiveStoreContext';
import { ctaMentesUtvonal } from '../constants/ctaMentesOldalak';

// Bolt-független sticky CTA → központi fő szám (constants/stores.js).
const PHONE_HREF = `tel:${PRIMARY_PHONE}`;
const PRIMARY_CALL_LABEL = `${STORES.belvaros.shortLabel} hívása: ${STORES.belvaros.phoneDisplay}`;
const MESSENGER_HREF = MESSENGER_URL;
const MAPS_HREF = 'https://www.google.com/maps/search/NorbiPhone+Orosha%CC%81za';

export default function MobileCTABar() {
  const location = useLocation();

  // Zárva-állapot: nyitvatartáson kívül a „Hívás” halott gomb – ilyenkor a
  // szerviz-oldal az értelmes elsődleges akció. Az állapot útvonalváltáskor
  // frissül; a forrás a constants/stores.js openingHours-a – egy
  // igazságforrás, nincs második nyitvatartás-lista.
  const [closed, setClosed] = useState(false);
  useEffect(() => { setClosed(!isAnyStoreOpenNow()); }, [location.pathname]);

  // KONTEXTUSFÜGGŐ SZÁM: ha a látogató épp egy konkrét készüléket néz
  // (terméklap vagy a listáról nyíló modál), annak az üzletnek a számát
  // hívja, ahol a készülék fizikailag van – korábban mindig a Belvárost
  // hívta, így a Tescós darabokról abba a boltba futott be a hívás, ahol
  // nem is látták a készüléket. Kontextus nélkül marad a fő szám.
  const activeStoreKey = useActiveStoreKey();
  const contact = activeStoreKey ? getStoreContact(activeStoreKey) : null;
  const callHref = contact ? contact.telHref : PHONE_HREF;
  const callAria = contact ? contact.callAriaLabel : PRIMARY_CALL_LABEL;

  // A mérőoldalakon a képernyő aljára ragadó sáv rátakarna a tesztfelületre.
  // MINDEN hook fölötte fut le, hogy a hook-sorrend renderenként azonos legyen.
  if (ctaMentesUtvonal(location.pathname)) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9990] sm:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div
        className="flex items-stretch border-t border-white/20"
        style={{
          background: 'rgba(29,29,31,0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        {closed ? (
          /* Zárva: a gomb neve és a célja UGYANAZ. Korábban „Visszahívás” volt
             a felirat, de a /szerviz oldalra vitt – a látogató visszahívást
             várt, és egy szolgáltatás-oldalt kapott. */
          <Link
            to="/szerviz/"
            onClick={() => trackCtaClick('mobil_sav_szerviz')}
            className="flex flex-col items-center justify-center gap-1 flex-1 py-3 text-white active:bg-white/10 transition-colors no-underline"
            data-testid="mobilebar-service"
          >
            <Wrench size={20} className="text-brand" aria-hidden="true" />
            <span className="text-[10px] font-semibold tracking-wide">Szerviz</span>
          </Link>
        ) : (
          <a
            href={callHref}
            onClick={() => trackCallClick('mobil_sav', { store: contact ? contact.store : 'belvaros' })}
            aria-label={callAria}
            data-testid="mobilebar-call"
            className="flex flex-col items-center justify-center gap-1 flex-1 py-3 text-white active:bg-white/10 transition-colors"
          >
            <Phone size={20} className="text-brand" aria-hidden="true" />
            <span className="text-[10px] font-semibold tracking-wide">Hívás</span>
          </a>
        )}

        <a
          href={MESSENGER_HREF}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackMessengerClick('mobil_sav')}
          className="flex flex-col items-center justify-center gap-1 flex-1 py-3 bg-brand text-white active:bg-brand-hover transition-colors"
        >
          <MessageCircle size={20} aria-hidden="true" />
          <span className="text-[10px] font-semibold tracking-wide">Messenger</span>
        </a>

        <a
          href={MAPS_HREF}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackDirectionsClick('mobil_sav')}
          className="flex flex-col items-center justify-center gap-1 flex-1 py-3 text-white active:bg-white/10 transition-colors"
        >
          <Navigation size={20} className="text-brand" aria-hidden="true" />
          <span className="text-[10px] font-semibold tracking-wide">Útvonal</span>
        </a>
      </div>
    </div>
  );
}
